import React, { useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Stepper,
  Step,
  StepLabel,
  Box,
  CircularProgress,
  Paper,
  Grid,
  Chip,
  Divider
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import {
  Warning as WarningIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  Info as InfoIcon
} from "@material-ui/icons";
import { toast } from "react-toastify";

const useStyles = makeStyles((theme) => ({
  dialog: {
    "& .MuiDialog-paper": {
      minWidth: "600px",
      maxWidth: "800px",
    },
  },
  stepContent: {
    padding: theme.spacing(3),
    minHeight: "300px",
  },
  warningBox: {
    padding: theme.spacing(2),
    backgroundColor: theme.palette.warning.light,
    borderRadius: theme.shape.borderRadius,
    marginBottom: theme.spacing(2),
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(2),
  },
  infoBox: {
    padding: theme.spacing(2),
    backgroundColor: theme.palette.info.light,
    borderRadius: theme.shape.borderRadius,
    marginBottom: theme.spacing(2),
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(2),
  },
  successBox: {
    padding: theme.spacing(2),
    backgroundColor: theme.palette.success.light,
    borderRadius: theme.shape.borderRadius,
    marginBottom: theme.spacing(2),
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(2),
  },
  errorBox: {
    padding: theme.spacing(2),
    backgroundColor: theme.palette.error.light,
    borderRadius: theme.shape.borderRadius,
    marginBottom: theme.spacing(2),
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(2),
  },
  statsGrid: {
    marginTop: theme.spacing(2),
  },
  statCard: {
    padding: theme.spacing(2),
    textAlign: "center",
    backgroundColor: theme.palette.background.default,
  },
  statValue: {
    fontSize: "2rem",
    fontWeight: "bold",
    color: theme.palette.primary.main,
  },
  statLabel: {
    fontSize: "0.875rem",
    color: theme.palette.text.secondary,
  },
}));

/**
 * ✅ FASE 2: Modal de preview de cambios de configuración
 * Muestra un proceso de 3 pasos para confirmar cambios que afectan tickets
 */
const ConfigChangePreviewModal = ({
  open,
  onClose,
  previewData,
  onConfirm,
  loading = false,
  type = "whatsapp" // "whatsapp" o "queue"
}) => {
  const classes = useStyles();
  const [activeStep, setActiveStep] = useState(0);
  const [applying, setApplying] = useState(false);
  const [result, setResult] = useState(null);

  const steps = ["Revisar cambios", "Confirmar impacto", "Aplicar cambios"];

  const handleNext = () => {
    setActiveStep((prevStep) => prevStep + 1);
  };

  const handleBack = () => {
    setActiveStep((prevStep) => prevStep - 1);
  };

  const handleApply = async () => {
    setApplying(true);
    try {
      const response = await onConfirm();
      setResult(response);
      handleNext();
    } catch (error) {
      toast.error("Error al aplicar cambios");
      console.error(error);
    } finally {
      setApplying(false);
    }
  };

  const handleClose = () => {
    setActiveStep(0);
    setResult(null);
    onClose();
  };

  // ✅ CORRECCIÓN (30/12/2025): Manejar onClose con reason para evitar warning de disableBackdropClick
  const handleDialogClose = (event, reason) => {
    if (applying) {
      // Si está aplicando cambios, no permitir cerrar
      return;
    }
    if (reason === "backdropClick" && activeStep !== 2) {
      // Si no está en el paso final, permitir cerrar con backdrop
      onClose();
    } else if (reason === "escapeKeyDown" && activeStep !== 2) {
      // Si no está en el paso final, permitir cerrar con ESC
      onClose();
    } else if (activeStep === 2) {
      // Si está en el paso final, usar handleClose
      handleClose();
    } else {
      onClose();
    }
  };

  // ✅ PASO 1: Revisar cambios
  const renderStep1 = () => (
    <Box className={classes.stepContent}>
      <Box className={classes.infoBox}>
        <InfoIcon />
        <Box>
          <Typography variant="h6">
            <span role="img" aria-label="info">ℹ️</span> Cambio de configuración detectado
          </Typography>
          <Typography variant="body2">
            Está a punto de modificar la configuración de automatización
          </Typography>
        </Box>
      </Box>

      <Typography variant="subtitle1" gutterBottom>
        <strong>Tipo de cambio:</strong>
      </Typography>
      <Chip
        label={
          previewData?.changeType === "remove_automation"
            ? "Quitar automatización"
            : previewData?.changeType === "change_automation_type"
            ? "Cambiar tipo de automatización"
            : "Sin cambios"
        }
        color={
          previewData?.changeType === "remove_automation"
            ? "secondary"
            : "primary"
        }
      />

      <Divider style={{ margin: "20px 0" }} />

      <Typography variant="subtitle1" gutterBottom>
        <strong>Impacto:</strong>
      </Typography>
      <Typography variant="body1" color="textSecondary">
        {previewData?.message}
      </Typography>

      {previewData?.affectedTickets > 0 && (
        <>
          <Divider style={{ margin: "20px 0" }} />
          <Typography variant="subtitle1" gutterBottom>
            <strong>Movimiento de tickets:</strong>
          </Typography>
          <Box display="flex" alignItems="center" gap={2}>
            <Chip label={previewData?.origin} color="primary" />
            <Typography>→</Typography>
            <Chip label={previewData?.destination} color="secondary" />
          </Box>
        </>
      )}
    </Box>
  );

  // ✅ PASO 2: Confirmar impacto
  const renderStep2 = () => (
    <Box className={classes.stepContent}>
      {previewData?.affectedTickets > 0 ? (
        <>
          <Box className={classes.warningBox}>
            <WarningIcon />
            <Box>
              <Typography variant="h6">
                <span role="img" aria-label="warning">⚠️</span> Atención: Se afectarán tickets activos
              </Typography>
              <Typography variant="body2">
                Este cambio modificará {previewData.affectedTickets} tickets
              </Typography>
            </Box>
          </Box>

          <Grid container spacing={2} className={classes.statsGrid}>
            <Grid item xs={4}>
              <Paper className={classes.statCard}>
                <Typography className={classes.statValue}>
                  {previewData.affectedTickets}
                </Typography>
                <Typography className={classes.statLabel}>
                  Total afectados
                </Typography>
              </Paper>
            </Grid>
            <Grid item xs={4}>
              <Paper className={classes.statCard}>
                <Typography className={classes.statValue}>
                  {previewData.ticketsByStatus.pending}
                </Typography>
                <Typography className={classes.statLabel}>
                  Pendientes
                </Typography>
              </Paper>
            </Grid>
            <Grid item xs={4}>
              <Paper className={classes.statCard}>
                <Typography className={classes.statValue}>
                  {previewData.ticketsByStatus.open}
                </Typography>
                <Typography className={classes.statLabel}>
                  En atención
                </Typography>
              </Paper>
            </Grid>
          </Grid>

          <Box mt={3}>
            <Typography variant="body2" color="textSecondary">
              <span role="img" aria-label="info">ℹ️</span> <strong>Nota importante:</strong> Los tickets que están siendo atendidos por un humano (status: "En atención") mantendrán su estado actual, pero se actualizarán sus flags de automatización.
            </Typography>
          </Box>
        </>
      ) : (
        <Box className={classes.successBox}>
          <CheckCircleIcon />
          <Box>
            <Typography variant="h6">
              <span role="img" aria-label="success">✅</span> Sin impacto en tickets
            </Typography>
            <Typography variant="body2">
              Este cambio no afectará ningún ticket activo
            </Typography>
          </Box>
        </Box>
      )}
    </Box>
  );

  // ✅ PASO 3: Aplicar cambios (resultado)
  const renderStep3 = () => (
    <Box className={classes.stepContent}>
      {applying ? (
        <Box display="flex" flexDirection="column" alignItems="center" justifyContent="center" minHeight="300px">
          <CircularProgress size={60} />
          <Typography variant="h6" style={{ marginTop: 20 }}>
            Aplicando cambios...
          </Typography>
          <Typography variant="body2" color="textSecondary">
            Por favor espere, esto puede tomar unos momentos
          </Typography>
        </Box>
      ) : result?.success ? (
        <>
          <Box className={classes.successBox}>
            <CheckCircleIcon />
            <Box>
              <Typography variant="h6">
                <span role="img" aria-label="success">✅</span> Cambios aplicados exitosamente
              </Typography>
              <Typography variant="body2">
                Los tickets han sido actualizados correctamente
              </Typography>
            </Box>
          </Box>

          <Grid container spacing={2} className={classes.statsGrid}>
            <Grid item xs={6}>
              <Paper className={classes.statCard}>
                <Typography className={classes.statValue} style={{ color: "#4caf50" }}>
                  {result.updatedTickets}
                </Typography>
                <Typography className={classes.statLabel}>
                  Tickets actualizados
                </Typography>
              </Paper>
            </Grid>
            <Grid item xs={6}>
              <Paper className={classes.statCard}>
                <Typography className={classes.statValue} style={{ color: result.errors > 0 ? "#f44336" : "#4caf50" }}>
                  {result.errors}
                </Typography>
                <Typography className={classes.statLabel}>
                  Errores
                </Typography>
              </Paper>
            </Grid>
          </Grid>
        </>
      ) : (
        <Box className={classes.errorBox}>
          <ErrorIcon />
          <Box>
            <Typography variant="h6">
              <span role="img" aria-label="error">❌</span> Error al aplicar cambios
            </Typography>
            <Typography variant="body2">
              Ocurrió un error durante la actualización. Por favor intente nuevamente.
            </Typography>
          </Box>
        </Box>
      )}
    </Box>
  );

  const getStepContent = (step) => {
    switch (step) {
      case 0:
        return renderStep1();
      case 1:
        return renderStep2();
      case 2:
        return renderStep3();
      default:
        return "Paso desconocido";
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleDialogClose}
      className={classes.dialog}
      maxWidth="md"
    >
      <DialogTitle>
        <span role="img" aria-label="settings">⚙️</span> Confirmación de cambios de configuración
      </DialogTitle>
      <DialogContent dividers>
        <Stepper activeStep={activeStep} alternativeLabel>
          {steps.map((label) => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>
        {getStepContent(activeStep)}
      </DialogContent>
      <DialogActions>
        {activeStep === 0 && (
          <>
            <Button onClick={onClose} color="default">
              Cancelar
            </Button>
            <Button
              onClick={handleNext}
              color="primary"
              variant="contained"
              disabled={!previewData || loading}
            >
              Continuar
            </Button>
          </>
        )}
        {activeStep === 1 && (
          <>
            <Button onClick={handleBack} color="default">
              Atrás
            </Button>
            <Button
              onClick={handleApply}
              color="primary"
              variant="contained"
              disabled={applying}
            >
              {previewData?.affectedTickets > 0 ? "Aplicar cambios" : "Confirmar"}
            </Button>
          </>
        )}
        {activeStep === 2 && (
          <Button onClick={handleClose} color="primary" variant="contained">
            Cerrar
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default ConfigChangePreviewModal;
